// Goods in > Suppliers (#152 step 3, migration 039): D365's raw-material vendors (groups RL and RI) with
// quality's settings about each - how certificates arrive, the reference quoted, the sender domains the
// intake job matches on (rule 25), the lot format (rule 18), where a COA request goes, and whether safe
// auto-approve may consider the supplier.
//
// D365 owns the vendor: the name, group, hold and country are read, never typed. A setting decides matching
// and approval, so editing needs RELEASE or SPEC_APPROVE (the server checks; the screen only says so).
// Rule 15: a list never read says "not known", and a failed refresh is shown beside the kept list.
import { useEffect, useMemo, useState } from 'react';
import { createColumnHelper } from '@tanstack/react-table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { DataTable } from '@/components/DataTable';
import { api, errorMessage } from '../../lib/api';
import { actionWithCode } from '../../lib/vocabulary';
import { useMyGrants, useResource } from '../../shells/OfficeShell';
import { TONE_CLASS, chipClass, stamp } from './goodsInModel';

export type CofaArrives = 'EMAIL' | 'ON_REQUEST' | 'HARD_COPY';
export type MatchKey = 'PO' | 'SO' | 'INVOICE' | 'BATCH';
export type LotFormat = 'EXACT' | 'DATE_CODE_PALLET' | 'REFERENCE_SUFFIX';
export type SupplierFlag = 'INTAKE_CANNOT_MATCH' | 'ON_HOLD' | 'NOT_IN_D365_LIST';

export interface SupplierSettings {
  cofaArrives: CofaArrives;
  matchKey: MatchKey;
  senderDomains: string | null;
  requestLeadDays: number | null;
  notes: string | null;
  contactEmail: string | null;
  lotFormat: LotFormat;
  autoApprove: boolean;
  /** Read with AI on its scans in Record by hand (migration 040). Absent from an older API. */
  scanPrefill?: boolean;
}

export interface Supplier {
  vendorAccount: string;
  vendorName: string;
  groupId: string | null;
  onHold: string | null;
  country: string | null;
  settings: SupplierSettings | null;
  receivedLines: number;
  lastCertificateAt: string | null;
  updatedByUpn: string | null;
  updatedAt: string | null;
  flags: SupplierFlag[];
}

export interface SuppliersPayload {
  syncedAt: string | null;
  lastAttempt: { ok: boolean; at: string | null; reason: string | null } | null;
  groups: string[];
  defaults: SupplierSettings;
  suppliers: Supplier[];
}

export const ARRIVES_LABEL: Record<CofaArrives, string> = { EMAIL: 'Emailed', ON_REQUEST: 'On request', HARD_COPY: 'With the goods' };
export const MATCH_LABEL: Record<MatchKey, string> = { PO: 'Our PO', SO: 'Their sales order', INVOICE: 'Their invoice', BATCH: 'The batch' };
/** The same, inside a sentence ("Emailed, quoting our PO"). */
const MATCH_PHRASE: Record<MatchKey, string> = { PO: 'our PO', SO: 'their sales order', INVOICE: 'their invoice', BATCH: 'the batch' };
export const LOT_LABEL: Record<LotFormat, string> = {
  EXACT: 'Lot is the batch',
  DATE_CODE_PALLET: 'Date code covers its pallets',
  REFERENCE_SUFFIX: 'Reference is the batch (PA107448-1)',
};
export const FLAG_LABEL: Record<SupplierFlag, string> = {
  INTAKE_CANNOT_MATCH: 'Intake cannot match its emails',
  ON_HOLD: 'On hold in D365',
  NOT_IN_D365_LIST: 'Not in D365 list',
};

/** What needs someone, for the tab's count: a supplier goods came from whose emails the intake job cannot match. */
export const needsSettings = (s: Supplier) => s.flags.includes('INTAKE_CANNOT_MATCH');

const col = createColumnHelper<Supplier>();

/** What the D365 vendor group codes mean (migration 039: "RL local, RI import", Amit). (UI/UX audit,
 *  31 Oct 2026, UX-48) The cell read "RL · AU" with nothing to say what RL is. */
export const GROUP_HELP = 'Account · D365 vendor group · country. Groups: RL = raw materials, local; RI = raw materials, import.';

export function GoodsInSuppliers({ onCount }: { onCount?: (n: number | null) => void } = {}) {
  const list = useResource<SuppliersPayload>('/suppliers');
  const grants = useMyGrants();
  const canEdit = grants.holds('RELEASE') || grants.holds('SPEC_APPROVE');
  const [editing, setEditing] = useState<Supplier | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [syncNote, setSyncNote] = useState<string | null>(null);
  const data = list.data;
  const rows = data?.suppliers ?? [];
  useEffect(() => onCount?.(list.error ? null : data ? rows.filter(needsSettings).length : null), [list.error, data, rows, onCount]);

  const columns = useMemo(
    () => [
      col.accessor((s) => `${s.vendorName} ${s.vendorAccount}`, {
        id: 'supplier',
        header: () => <span title={GROUP_HELP}>Supplier</span>,
        cell: (c) => {
          const s = c.row.original;
          return (
            <>
              <span className="block">{s.vendorName}</span>
              <span className="block font-mono text-(length:--fs-xs) text-(--muted)" title={GROUP_HELP}>
                {s.vendorAccount} · {s.groupId ?? '—'} · {s.country ?? '—'}
              </span>
            </>
          );
        },
        meta: { rowHeader: true, width: '16rem' },
      }),
      col.accessor((s) => s.flags.map((f) => FLAG_LABEL[f]).join(' '), {
        id: 'flags',
        header: 'Needs',
        cell: (c) => (
          <span className="flex flex-wrap gap-(--s1)">
            {c.row.original.flags.map((f) => (
              <span key={f} className={`${chipClass} ${TONE_CLASS.warn}`}>
                {FLAG_LABEL[f]}
              </span>
            ))}
          </span>
        ),
        meta: { width: '12rem' },
      }),
      col.accessor((s) => (s.settings ? ARRIVES_LABEL[s.settings.cofaArrives] : ''), {
        id: 'arrives',
        header: 'Certificates',
        cell: (c) => {
          const st = c.row.original.settings;
          return st ? `${ARRIVES_LABEL[st.cofaArrives]}, quoting ${MATCH_PHRASE[st.matchKey]}` : <span className="text-(--muted)">No settings yet</span>;
        },
      }),
      col.accessor((s) => s.settings?.senderDomains ?? '', { id: 'domains', header: 'Sender domains', cell: (c) => c.getValue() || '—', meta: { mono: true } }),
      col.accessor((s) => (s.settings ? LOT_LABEL[s.settings.lotFormat] : ''), { id: 'lot', header: 'Lot format', cell: (c) => c.getValue() || '—' }),
      col.accessor((s) => (s.settings ? (s.settings.autoApprove ? 'On' : 'Off') : 'On'), { id: 'auto', header: 'Auto-approve', meta: { nowrap: true } }),
      col.accessor((s) => (s.settings?.scanPrefill ? 'On' : 'Off'), { id: 'scan', header: 'Read scans with AI', meta: { nowrap: true } }),
      col.accessor((s) => s.receivedLines, { id: 'lines', header: 'Lines received', meta: { nowrap: true } }),
      col.accessor((s) => s.lastCertificateAt ?? '', { id: 'lastCert', header: 'Last certificate', cell: (c) => (c.getValue() ? stamp(c.getValue()) : '—'), meta: { nowrap: true } }),
      col.display({
        id: 'edit',
        header: '',
        cell: (c) => (
          // (UI/UX audit, 31 Oct 2026, UX-45) Twenty buttons all named "Edit" gave a screen reader
          // no row: the name says whose settings it opens.
          <Button
            type="button"
            variant="outline"
            size="sm"
            aria-label={`Edit settings for ${c.row.original.vendorName}`}
            disabled={!canEdit}
            // In words, codes kept (UI/UX audit, 31 Oct 2026, UX-17).
            title={canEdit ? undefined : `Changing supplier settings needs the ${actionWithCode('RELEASE')} or ${actionWithCode('SPEC_APPROVE')} authorisation.`}
            onClick={() => setEditing(c.row.original)}
          >
            Edit
          </Button>
        ),
        meta: { nowrap: true, width: '5rem' },
      }),
    ],
    [canEdit],
  );

  const refresh = async () => {
    setSyncing(true);
    setSyncNote(null);
    try {
      const r = await api.post<{ ok: boolean; vendors?: number; reason?: string }>('/suppliers/sync');
      setSyncNote(r.ok ? `Read ${r.vendors} vendors from D365.` : `D365 could not be read (${r.reason}); the list below is the last one read.`);
      list.reload();
    } catch (err) {
      setSyncNote(`Refresh failed: ${errorMessage(err)}`);
    } finally {
      setSyncing(false);
    }
  };

  return (
    <section aria-labelledby="suppliers-heading" className="flex flex-col gap-(--s3)">
      <div className="flex flex-wrap items-end justify-between gap-(--s2)">
        <div>
          <h2 id="suppliers-heading" className="m-0 font-display text-[22px] font-semibold leading-tight">
            Suppliers
          </h2>
          <p className="m-0 mt-(--s1) max-w-[80ch] text-(length:--fs-sm) text-(--muted)">
            How each supplier&apos;s certificates are handled.{' '}
            {data ? (data.syncedAt ? `Vendors read from D365 ${stamp(data.syncedAt)}.` : 'Vendors not read from D365 yet.') : ''}
          </p>
          {data?.lastAttempt && !data.lastAttempt.ok ? (
            <p role="status" className={`m-0 mt-(--s2) rounded-(--radius) border p-(--s2) text-(length:--fs-sm) font-semibold ${TONE_CLASS.warn}`}>
              The last refresh from D365 failed ({data.lastAttempt.reason ?? 'reason not known'}, {stamp(data.lastAttempt.at)}): the list below is the last one read.
            </p>
          ) : null}
          {syncNote ? (
            <p role="status" className="m-0 mt-(--s1) text-(length:--fs-sm)">
              {syncNote}
            </p>
          ) : null}
        </div>
        {/* (UI/UX audit, 31 Oct 2026, UX-24) Named for what it reads: the register has its own
            "Refresh from D365" (receipts), and two buttons with one name did different things. */}
        <Button type="button" variant="outline" disabled={!canEdit || syncing} onClick={() => void refresh()}>
          {syncing ? 'Reading D365…' : 'Refresh vendor list from D365'}
        </Button>
      </div>
      {list.error ? (
        <p role="alert" className={`m-0 flex flex-wrap items-center gap-(--s3) rounded-(--radius) border p-(--s3) text-(length:--fs-sm) font-semibold ${TONE_CLASS.warn}`}>
          Could not read the suppliers: {list.error}
          <Button variant="outline" type="button" onClick={() => list.reload()}>
            Try again
          </Button>
        </p>
      ) : (
        <DataTable
          data={rows}
          columns={columns}
          caption="Suppliers, most goods received first"
          getRowId={(s) => s.vendorAccount}
          loading={list.loading}
          search="Find a supplier"
          empty={data ? (data.syncedAt ? 'No vendor in groups RL or RI.' : 'Not known: the vendor list has not been read from D365. Refresh vendor list from D365.') : null}
        />
      )}
      <Sheet open={editing !== null} onOpenChange={(o) => !o && setEditing(null)}>
        <SheetContent side="right" className="flex w-full flex-col gap-(--s2) overflow-y-auto sm:max-w-[min(640px,92vw)]">
          {editing && data ? (
            <SupplierForm
              supplier={editing}
              defaults={data.defaults}
              onSaved={() => {
                setEditing(null);
                list.reload();
              }}
            />
          ) : null}
        </SheetContent>
      </Sheet>
    </section>
  );
}

function SupplierForm({ supplier, defaults, onSaved }: { supplier: Supplier; defaults: SupplierSettings; onSaved: () => void }) {
  const start = supplier.settings ?? defaults;
  const [f, setF] = useState({
    cofaArrives: start.cofaArrives,
    matchKey: start.matchKey,
    senderDomains: start.senderDomains ?? '',
    requestLeadDays: start.requestLeadDays === null ? '' : String(start.requestLeadDays),
    contactEmail: start.contactEmail ?? '',
    lotFormat: start.lotFormat,
    autoApprove: start.autoApprove,
    scanPrefill: start.scanPrefill ?? false,
    notes: start.notes ?? '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const lead = f.requestLeadDays.trim() === '' ? null : Number(f.requestLeadDays);
  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await api.put(`/suppliers/${encodeURIComponent(supplier.vendorAccount)}`, { ...f, requestLeadDays: lead });
      onSaved();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };
  // (UI/UX audit, 31 Oct 2026, UX-13) 44px like the inputs beside them; h-9 (36px) was under the office floor.
  const select = 'min-h-[44px] rounded-(--radius) border border-(--border) bg-transparent px-(--s2) text-(length:--fs-sm)';
  return (
    <>
      <SheetHeader>
        <SheetTitle>{supplier.vendorName}</SheetTitle>
        <SheetDescription className="font-mono">
          {supplier.vendorAccount} · D365 group {supplier.groupId ?? '—'} · {supplier.onHold && supplier.onHold !== 'No' ? `on hold (${supplier.onHold})` : 'not on hold'}
        </SheetDescription>
      </SheetHeader>
      <form
        className="flex flex-col gap-(--s3) text-(length:--fs-sm)"
        onSubmit={(e) => {
          e.preventDefault();
          if (!saving) void save();
        }}
      >
        <Label className="flex flex-col gap-(--s1)">
          How certificates arrive
          <select className={select} value={f.cofaArrives} onChange={(e) => setF({ ...f, cofaArrives: e.target.value as CofaArrives })}>
            {(Object.keys(ARRIVES_LABEL) as CofaArrives[]).map((k) => (
              <option key={k} value={k}>
                {ARRIVES_LABEL[k]}
              </option>
            ))}
          </select>
        </Label>
        <Label className="flex flex-col gap-(--s1)">
          The reference they quote
          <select className={select} value={f.matchKey} onChange={(e) => setF({ ...f, matchKey: e.target.value as MatchKey })}>
            {(Object.keys(MATCH_LABEL) as MatchKey[]).map((k) => (
              <option key={k} value={k}>
                {MATCH_LABEL[k]}
              </option>
            ))}
          </select>
        </Label>
        <Label className="flex flex-col gap-(--s1)">
          Sender domains (the intake job matches their emails by these)
          {/* (UI/UX audit, 31 Oct 2026, UX-48) A generic example: two real suppliers' domains as the
              placeholder read like this supplier's values already filled in. */}
          <Input value={f.senderDomains} placeholder="e.g. supplier.com" onChange={(e) => setF({ ...f, senderDomains: e.target.value })} />
        </Label>
        <Label className="flex flex-col gap-(--s1)">
          Where a COA request goes
          <Input type="email" value={f.contactEmail} placeholder="their QA address, never accounts payable" onChange={(e) => setF({ ...f, contactEmail: e.target.value })} />
        </Label>
        <Label className="flex flex-col gap-(--s1)">
          Ask this many days before delivery
          <Input inputMode="numeric" value={f.requestLeadDays} onChange={(e) => setF({ ...f, requestLeadDays: e.target.value })} />
        </Label>
        <Label className="flex flex-col gap-(--s1)">
          Lot format
          <select className={select} value={f.lotFormat} onChange={(e) => setF({ ...f, lotFormat: e.target.value as LotFormat })}>
            {(Object.keys(LOT_LABEL) as LotFormat[]).map((k) => (
              <option key={k} value={k}>
                {LOT_LABEL[k]}
              </option>
            ))}
          </select>
        </Label>
        <label className="flex items-center gap-(--s2)">
          <input type="checkbox" checked={f.autoApprove} onChange={(e) => setF({ ...f, autoApprove: e.target.checked })} />
          Auto-approve may consider this supplier&apos;s certificates (results only; a person can reject any time)
        </label>
        <label className="flex items-center gap-(--s2)">
          <input type="checkbox" checked={f.scanPrefill} onChange={(e) => setF({ ...f, scanPrefill: e.target.checked })} />
          Offer Read with AI on its scanned certificates (a person checks every value before saving)
        </label>
        <Label className="flex flex-col gap-(--s1)">
          Notes
          <textarea className="min-h-20 rounded-(--radius) border border-(--border) bg-transparent p-(--s2)" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} />
        </Label>
        {supplier.updatedByUpn ? (
          <p className="m-0 text-(length:--fs-xs) text-(--muted)">
            Last changed by {supplier.updatedByUpn} {stamp(supplier.updatedAt)}.
          </p>
        ) : null}
        {error ? (
          <p role="alert" className="m-0 font-semibold">
            Not saved: {error}
          </p>
        ) : null}
        <div>
          <Button type="submit" disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </form>
    </>
  );
}
